import { useMemo, useRef, useState } from "react";
import { Download, Upload } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { DatabookGenerationStage } from "./DatabookGenerationStage";
import type { DatabookCellSelection } from "./databookCellSelection";
import { loadLastDatabookSheet, saveLastDatabookSheet, selectedDatabookSheet, toggleDatabookOutlineGroup } from "./databookPreviewState";
import { DEMO_DATABOOK_DOWNLOAD, loadDemoDatabookManifest, loadDemoDatabookSheet, projectDemoDatabookSheet } from "./demoDatabook";

const DEMO_KEY = "demo-stallion-databook";
// The workbook has no persisted analysis-job Q&A or variance records.
const DEMO_INSIGHTS = { version_id: DEMO_KEY, questions: [], variances: [] };

export function DemoDatabookStage({ onBack }: { onBack: () => void }) {
  const uploadInput = useRef<HTMLInputElement>(null);
  const manifest = useQuery({
    queryKey: [DEMO_KEY, "manifest"],
    queryFn: ({ signal }) => loadDemoDatabookManifest(signal),
    staleTime: 0,
  });
  const [choice, setChoice] = useState(() => loadLastDatabookSheet(DEMO_KEY));
  const [rowOffset, setRowOffset] = useState(0);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [expandedColumns, setExpandedColumns] = useState<string[]>([]);
  const [selection, setSelection] = useState<DatabookCellSelection | null>(null);
  const selectedSheet = selectedDatabookSheet(manifest.data?.sheets, choice);
  const entry = selectedSheet ? manifest.data?.previews[selectedSheet] : undefined;
  const expand = Boolean(entry?.full && (expandedRows.length || expandedColumns.length));
  const path = entry?.pages ? entry.pages[String(rowOffset)] : expand ? entry?.full : entry?.base;
  const preview = useQuery({
    queryKey: [DEMO_KEY, manifest.data?.sha256, path],
    queryFn: ({ signal }) => loadDemoDatabookSheet(path!, manifest.data!.sha256, signal),
    enabled: Boolean(path),
    staleTime: Infinity,
  });
  const sheet = useMemo(() => preview.data
    ? expand ? projectDemoDatabookSheet(preview.data, expandedRows, expandedColumns) : preview.data
    : null, [preview.data, expand, expandedRows, expandedColumns]);

  return <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
    <DatabookGenerationStage
      toolbarActions={
        <>
        <input
          ref={uploadInput}
          type="file"
          accept=".xlsx"
          className="hidden"
          aria-label="Upload databook"
          onChange={event => { event.currentTarget.value = ""; }}
        />
        <button
          type="button"
          onClick={() => uploadInput.current?.click()}
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border px-3 py-2 font-medium text-foreground/80 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{ fontSize: 12 }}
        >
          <Upload aria-hidden="true" size={12} /> Upload
        </button>
        <a href={manifest.data ? `${DEMO_DATABOOK_DOWNLOAD}?v=${manifest.data.sha256}` : undefined} download="Stallion_Demo.xlsx"
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-foreground text-background font-semibold px-3 py-2 hover:opacity-90 shadow-sm"
          style={{ fontSize: 12 }} title="Export the databook">
          <Download aria-hidden="true" size={12} /> Download databook
        </a>
        </>
      }
      state={{ status: "ready" }}
      insights={DEMO_INSIGHTS}
      sheetNames={manifest.data?.sheets ?? null}
      sheetItems={manifest.data?.items ?? null}
      alertSheetNames={manifest.data?.alert_sheets ?? []}
      sheetListError={manifest.isError}
      selectedSheetName={selectedSheet}
      sheetPreview={sheet}
      sheetPreviewLoading={manifest.isPending || (Boolean(path) && preview.isPending)}
      sheetPreviewFetching={preview.isFetching}
      sheetPreviewError={preview.isError}
      selection={selection}
      canEdit={false}
      onRetry={() => { void manifest.refetch(); }}
      onBack={onBack}
      onSelectSheet={name => {
        setChoice(name);
        saveLastDatabookSheet(DEMO_KEY, name);
        setRowOffset(0);
        setExpandedRows([]);
        setExpandedColumns([]);
        setSelection(null);
      }}
      onSelectSheetPage={offset => { setRowOffset(offset); setSelection(null); }}
      onToggleOutlineGroup={id => {
        setExpandedRows(previous => toggleDatabookOutlineGroup(previous, id, sheet?.outline_groups ?? []));
        setSelection(null);
      }}
      onToggleColumnOutlineGroup={id => {
        setExpandedColumns(previous => toggleDatabookOutlineGroup(previous, id, sheet?.column_outline_groups ?? []));
        setSelection(null);
      }}
      onRetrySheet={() => { void preview.refetch(); }}
      onSelectionChange={setSelection}
    />
  </div>;
}
